// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { AfterViewInit, Component, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { ActivatedRoute, Router } from '@angular/router';
import { Dealer, DealerProductEnrollments, DealerProducts } from '@interfaces/dealer.interfaces';
import {
    Assignees,
    DdjDocumentBasic,
    DdjDocumentCore,
    DdjDocumentRequirementFields,
    DdjDocumentRequirements,
    DdjTemplate,
    DocumentStatus,
    FormDocumentUploadType,
    InternalDocumentType,
    TemplateUploadType,
} from '@interfaces/document.interfaces';
import { AppEvent } from '@interfaces/interfaces';
import { DealerAppPermissions, User, UserPermissionIds, UserTypes } from '@interfaces/user.interfaces';
import { DealershipService } from '@services/dealership.service';
import { DocumentsService } from '@services/documents.service';
import { ErrorService } from '@services/error.service';
import { FeatureFlagService } from '@services/feature-flag.service';
import { NavService } from '@services/nav.service';
import { PdftronInteractionsService } from '@services/pdftron-interactions.service';
import { SettingsService } from '@services/settings.service';

import { catchError, debounceTime, distinctUntilChanged, firstValueFrom, Observable, of, takeWhile, tap } from 'rxjs';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { DeleteTemplateModalComponent } from 'src/app/modals/delete-template-modal/delete-template-modal.component';
import { FileSizeErrorModalComponent } from 'src/app/modals/file-size-error-modal/file-size-error-modal.component';
import { SuccessModalComponent } from 'src/app/modals/success-modal/success-modal.component';
import { setTemplateUploadTypeList } from 'src/app/utils/document-utils';
import { showFormErrors } from 'src/app/utils/form-utils';
import { prepareFileForUploadBase64 } from 'src/app/utils/pdf-form-utils';
import { scrollIntoView } from 'src/app/utils/utils';

@Component({
    selector: 'forms',
    templateUrl: './forms.component.html',
    styleUrls: ['./forms.component.scss'],
})
export class FormsComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @ViewChild(FormGroupDirective) formGroupDirective: FormGroupDirective;
    @ViewChild('helpPanel') helpPanel: TemplateRef<any>;
    @Input() dealDocsTemplateId: any[] = [];
    @Input() dealDocsCore: DdjDocumentBasic[] = [];
    @Input() dealId: number = null;
    public documentForm: FormGroup;
    private isComponentAlive = true;
    public isLoading = false;
    public coreForms: DdjDocumentCore[] = [];
    public createDocument = true;

    public disableDeleteBtn = false;
    canHandleSystemDocuments = false;
    docId = 0;
    document: any; // better type
    internalDocumentTypeSelected: InternalDocumentType = null;
    internalDocumentNotSelected: any;
    formErrors: any = {};

    templateUploadType = <TemplateUploadType[]>[];
    formDocumentUploadType = FormDocumentUploadType;
    fileName = 'Choose File';
    selectedPdf: any = null;
    selectedPdfIsCommonDocument = false;
    selectedPdfIsSystemDocument = false;
    selectedPdfHasDocUrl = false;
    isDeleteAllowed = false;
    isDealerOnly = false;
    assignees = Assignees;
    pdfDataSet = false;
    templateData = {
        offsetX: null,
        offsetY: null,
        documentId: null,
    };
    title: string = null;
    type = 'Create';
    types: InternalDocumentType[] = [];
    pdfFields: any = {};
    public user: User = null;
    cleanedFileSize: number = null;
    cleanedBase64: string = null;
    apiSigningEnabled: any;
    tenantSupportPhone: string;
    tenantSupportEmail: string;
    createClicked = false;
    selectedFormType: 'predefined-form' | 'upload-during-deal' | 'print-from-pdf' | 'print-from-impact' | 'manually-filled-during-deal';
    isInErrorState = false;
    private dealerEnrollments: DealerProductEnrollments[] = [];

    constructor(
        private readonly dialog: MatDialog,
        private readonly documentsService: DocumentsService,
        private readonly route: ActivatedRoute,
        private readonly fb: FormBuilder,
        private readonly errorService: ErrorService,
        private readonly navService: NavService,
        private readonly router: Router,
        private readonly pits: PdftronInteractionsService,
        private readonly featureFlag: FeatureFlagService,
        private readonly dealershipService: DealershipService,
        private readonly settingsService: SettingsService
    ) {
        this.apiSigningEnabled = this.featureFlag.getDefaultFeatureConfiguration('apiSigning');
    }
    ngAfterViewInit(): void {
        this.navService.setHelpPanel(this.helpPanel);
    }

    @HostListener('window:beforeunload')
    canDeactivate(): Observable<boolean> | boolean {
        const fieldsTouched = this.title === 'edited-existing' || this.title === 'dealer-edited-existing';
        return !this.documentForm.dirty && !fieldsTouched;
    }

    ngOnInit(): void {
        this.coreForms = this.route.snapshot.data.coreForms;
        this.user = this.route.snapshot.data.user;
        this.types = this.route.snapshot.data.internalDocTypes;

        if (!this.userIsInternal) {
            this.defaultDocTypeToOther();
        }

        const uploadTypes = this.route.snapshot.data.uploadTypes;
        this.templateUploadType = setTemplateUploadTypeList(uploadTypes);
        this.buildForm();
        this.docId = 0;
        this.navService.setTitle('F&I Forms');

        if (history.state.title) {
            this.setPageInfo(history);
        }

        this.canHandleSystemDocuments = this.handleSystemDocumentsCheck();

        const { dealerId }: Dealer = this.route.snapshot.data.dealerships;
        this.dealershipService
            .getDealerEnrollment$(dealerId)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((enrollments) => {
                this.dealerEnrollments = enrollments;
            });

        this.dealershipService.dealer$.pipe(takeWhile(() => this.isComponentAlive)).subscribe(async (dealer) => {
            ({ supportPhone: this.tenantSupportPhone, supportEmail: this.tenantSupportEmail } = await this.settingsService.getOemSettings(dealer.oemCode));
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.dealDocsCore.currentValue.length) {
            this.dealDocsCore = changes.dealDocsCore.currentValue;
            this.type = 'Add';
        }
    }

    ngOnDestroy(): void {
        this.navService.setHelpPanel(null);
        this.isComponentAlive = false;
    }

    get errorState(): boolean {
        return this.isInErrorState;
    }

    defaultDocTypeToOther(): void {
        this.internalDocumentTypeSelected = this.types.find((x) => x.internalDocumentName === 'Other');
        this.internalDocumentNotSelected = false;
    }

    userIsInternal(): boolean {
        return this.user.userType === UserTypes.Internal;
    }

    userIsDealer(): boolean {
        return this.user.userType === UserTypes.Dealer;
    }

    async dealerHasPrintToDdj(): Promise<boolean> {
        const activation = this.dealerEnrollments.find((x) => x.productId === DealerProducts.EzDocsAutomate)?.activationDate;
        const turnOffAccess = this.dealerEnrollments.find((x) => x.productId === DealerProducts.EzDocsAutomate)?.turnOffAccessDate;
        const currentDate = new Date();
        let hasPrintToDDJ = false;

        if (activation && new Date(activation) < currentDate) {
            if (turnOffAccess && new Date(turnOffAccess) < currentDate) {
                hasPrintToDDJ = false;
            } else {
                hasPrintToDDJ = true;
            }
        } else {
            hasPrintToDDJ = false;
        }

        return hasPrintToDDJ;
    }

    isCommonDoc(): boolean {
        return this.selectedPdfIsCommonDocument;
    }

    isSystemDoc(): boolean {
        return this.selectedPdfIsSystemDocument;
    }

    getFormName(form: TemplateUploadType['uploadTypeId']) {
        switch (form) {
            case 1:
                return 'Form Ready for Signatures';
            case 2:
                return 'Manually Uploaded During Deal';
            case 3:
                return 'Print From Laser Form';
            case 4:
                return 'Print From Impact Form';
            case 5:
                return 'Manually Filled During Deal';
            case 7:
                return 'Consumer Documents';
            default:
                return 'Print From Impact Form';
        }
    }

    private handleSystemDocumentsCheck(): boolean {
        const permissions = (this.user.dealerAppPermissions || []).find((x) => x.dealerId === this.user.dealerId);
        const isInternalDdjAdmin =
            this.user.userType === UserTypes.Internal && permissions && permissions.userPermissionId.some((x) => x === UserPermissionIds.DDJAdmin);

        return isInternalDdjAdmin;
    }

    isUserCurrentDealerPermission(): DealerAppPermissions {
        return (this.user.dealerAppPermissions || []).find((x) => x.dealerId === this.user.dealerId);
    }

    private allowTemplateDelete(template: DdjTemplate): boolean {
        // if they are adding a new form to a deal they shouldnt be able to delete the form
        return !window.location.pathname.includes('add') && !template.systemDocument;
    }

    private buildForm(): void {
        this.documentForm = this.fb.group({
            name: [
                null,
                {
                    validators: [Validators.required, Validators.maxLength(64)],
                    updateOn: 'blur',
                },
            ],
            preamble: [
                null,
                {
                    validators: [Validators.maxLength(2000)],
                    updateOn: 'blur',
                },
            ],
            isDealerOnly: [false],
            isRequiredForAll: [false],
            isNew: [false],
            isUsed: [false],
            isBuy: [false],
            isLease: [false],
            isCash: [false],
            isFinance: [false],
            isLeaseTurnIn: [false],
            isTradeIn: [false],
            fileProvidedBy: [Assignees.dealer],
            typeOfDocument: [1],
        });

        this.onChanges();
    }

    public createNew(createClicked = true): void {
        this.dirtyFormCheck(() => {
            this.createClicked = createClicked;
            this.buildForm();
            this.formGroupDirective.resetForm();
            this.internalDocumentTypeSelected = null;
            this.internalDocumentNotSelected = false;
            this.docId = null;
            this.document = null;
            this.selectedPdfHasDocUrl = false;
            this.formErrors = {};
            this.pdfFields = {};
            this.pits.fields = {};
            this.selectedPdfIsCommonDocument = false;
            this.selectedPdfIsSystemDocument = false;
            this.cancelUpload();
            this.isDeleteAllowed = false;
        });
    }

    dirtyFormCheck(callback: () => void) {
        if (!this.documentForm.dirty) {
            callback();
        } else {
            this.dialog
                .open(ConfirmModalComponent, {
                    width: '700px',
                    data: {
                        title: 'You Have Unsaved Changes',
                        message:
                            'You have made modifications to this F&I Form that have not been saved. If you do not want to keep them, click Confirm. Otherwise, click Cancel and continue modifying and / or save the form.',
                        btnText: 'Confirm',
                    },
                })
                .afterClosed()
                .subscribe((confirmed: boolean) => {
                    if (confirmed) {
                        this.documentForm.markAsPristine();
                        callback();
                    }
                });
        }
    }

    public cancelUpload() {
        this.fileName = 'Choose File';
        this.selectedPdf = null;
        this.pdfDataSet = false;
        this.isInErrorState = false;
        this.radioChange({ source: undefined, value: this.documentForm.controls.typeOfDocument.value });
    }

    private checkForPDF(): boolean {
        // if pdf is required but no pdf is selected
        if (
            !this.selectedPdf &&
            this.documentForm.controls.typeOfDocument.value !== FormDocumentUploadType.Uploaded_During_Deal &&
            this.documentForm.controls.typeOfDocument.value !== FormDocumentUploadType.Consumer_Documents
        ) {
            this.formErrors.uploadFile = `You must choose a file or select "${
                this.templateUploadType.find((x) => x.uploadTypeId === FormDocumentUploadType.Uploaded_During_Deal).uploadTypeDescription
            }" or "${this.templateUploadType.find((x) => x.uploadTypeId === FormDocumentUploadType.Consumer_Documents).uploadTypeDescription}".`;
            return false;
        } else {
            delete this.formErrors.uploadFile;
            return true;
        }
    }

    public deleteTemplate() {
        this.dialog
            .open(DeleteTemplateModalComponent, {
                width: '700px',
                closeOnNavigation: false,
                data: {
                    DdjTemplate: this.coreForms.find((x) => x.documentId === this.docId),
                    dealerId: this.user.dealerId,
                },
            })
            .beforeClosed()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((val) => {
                if (val && val.length > 0) {
                    this.coreForms = val;
                }

                this.createNew();
                this.isDeleteAllowed = false;
            });
    }

    public editForm(doc: any): void {
        this.dirtyFormCheck(() => {
            this.createClicked = false;
            const currentSelectedDoc = this.coreForms.find((x) => x.documentId === doc.documentId);

            this.documentsService
                .getFullTemplateData$(currentSelectedDoc.documentId, true)
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe((fullTemplate) => {
                    doc = fullTemplate;

                    if (doc.documentSignedURL) {
                        this.selectedPdfHasDocUrl = true;
                    }

                    this.templateData.offsetX = doc.offsetX;
                    this.templateData.offsetY = doc.offsetY;

                    this.selectedPdf = null;
                    this.pdfDataSet = false;
                    this.pdfFields = {};

                    doc.status = { ...doc.status, selected: true };

                    // needed to trigger the pdf loader
                    setTimeout(() => {
                        const formData = {
                            ...doc.deal,
                            docId: doc.documentId,
                            name: doc.displayName,
                            preamble: doc.preamble,
                            isDealerOnly: doc.requirements.isDealerOnly,
                            internalDoc: doc.internalDocumentTypeId,
                            fileProvidedBy: this.getFileProvidedBy(doc.requirements),
                            typeOfDocument: this.getInternalDocType(doc.uploadTypeId)[0]?.uploadTypeId,
                        };

                        this.selectedPdfIsCommonDocument = doc.commonDocument;
                        this.selectedPdfIsSystemDocument = doc.systemDocument;
                        this.pdfFields = doc.inputFieldSettings;

                        if (doc.fileName) {
                            this.selectedPdf = doc;
                            this.pdfDataSet = true;
                            this.title = 'dealer-view-template';
                            this.fileName = doc.fileName;
                        }

                        this.isDeleteAllowed = this.allowTemplateDelete(doc) || this.handleSystemDocumentsCheck();
                        this.setForm(formData);
                    }, 50);
                });
        });
    }

    public eventHandler(event: AppEvent): void {
        if (this.title === 'dealer-view-template' || this.title === 'cancel-existing-edit' || this.title === 'dealer-cleaned') {
            this.title = 'edit-existing';
        } else if (this.title === 'dealer-edited-existing') {
            this.title = 'edited-existing';
        } else {
            this.title = 'edit-create';
        }

        switch (event.context) {
            case 'file-error':
            case 'isErrorState':
                this.pdfDataSet = false;
                this.isInErrorState = true;
                break;
            case 'edit-create':
            case 'edit-existing':
            case 'edited-existing':
                if (window.location.pathname.includes('deals')) {
                    this.router.navigate(['dashboard', 'deals', this.dealId, 'add', 'edit'], {
                        state: {
                            docId: this.docId,
                            file: event.data.document,
                            form: this.documentForm.value,
                            fields: event.data.fields,
                            internalDoc: this.internalDocumentTypeSelected,
                            title: 'edit-deal-doc',
                            isErrorState: this.isInErrorState,
                        },
                    });
                } else {
                    this.router.navigate(['dashboard', 'forms', 'edit'], {
                        state: {
                            docId: this.docId,
                            file: event.data.document,
                            form: this.documentForm.value,
                            fields: event.data.fields,
                            internalDoc: this.internalDocumentTypeSelected,
                            title: this.title,
                            isErrorState: this.isInErrorState,
                        },
                    });
                }
                break;
            default:
                break;
        }
    }

    private getFileProvidedBy(requirements: any): string {
        if (requirements.needsBuyerUpload) {
            return 'buyer';
        }

        if (requirements.needsCoBuyerUpload) {
            return 'cobuyer';
        }

        return 'dealer';
    }

    private getInternalDocType(formControl?: any): TemplateUploadType[] {
        const control = formControl ? formControl : this.documentForm.controls.typeOfDocument.value;

        this.templateUploadType.filter((t) => control === t.uploadTypeId);

        return this.templateUploadType.filter((t) => control === t.uploadTypeId);
    }

    public internalDocumentSelectedChanged(event: AppEvent): void {
        this.internalDocumentTypeSelected = event.data;
        this.documentForm.patchValue({
            name: event.data.internalDocumentName,
            preamble: event.data.suggestedPreamble,
        });
        this.updateFormValidity();
        this.documentForm.markAsDirty();
        this.internalDocumentNotSelected = false;
    }

    public isPreventCopy(form: DdjDocumentCore): boolean {
        return !!this.dealDocsCore.find((doc) => {
            const dealDocTemplateId = this.dealDocsTemplateId.find((x) => x.templateId === form.documentId && x.documentId === doc.documentId);
            return form.documentId === dealDocTemplateId?.templateId && doc.documentStatusId !== DocumentStatus.Deleted;
        });
    }

    private isValid(): boolean {
        // Errors being caught are ordered from top to bottom
        this.internalDocumentNotSelected = !this.internalDocumentTypeSelected ? true : false;

        if (!this.internalDocumentTypeSelected) {
            if (this.userIsInternal()) {
                scrollIntoView('.top-of-form');
                return false;
            }
            this.defaultDocTypeToOther();
        }

        if (this.documentForm.status === 'INVALID') {
            showFormErrors(this.documentForm.controls);
            return false;
        }

        const pdfIsGood = this.checkForPDF();

        if (!pdfIsGood) {
            return false;
        }

        return true;
    }

    private onChanges(): void {
        this.documentForm.valueChanges
            .pipe(
                takeWhile(() => this.isComponentAlive),
                debounceTime(500),
                distinctUntilChanged()
            )
            .subscribe(() => {
                if (this.isCommonDoc() || (this.isSystemDoc() && !this.userIsInternal())) {
                    this.documentForm.controls.name.disable();
                    this.documentForm.controls.preamble.disable();
                    this.documentForm.controls.isNew.disable();
                    this.documentForm.controls.isUsed.disable();
                    this.documentForm.controls.isBuy.disable();
                    this.documentForm.controls.isLease.disable();
                    this.documentForm.controls.isCash.disable();
                    this.documentForm.controls.isFinance.disable();
                    this.documentForm.controls.isLeaseTurnIn.disable();
                    this.documentForm.controls.isTradeIn.disable();
                    this.documentForm.controls.isRequiredForAll.disable();
                    this.documentForm.controls.typeOfDocument.disable();
                    this.documentForm.controls.fileProvidedBy.disable();
                    this.documentForm.controls.isDealerOnly.disable();
                } else {
                    this.documentForm.controls.name.enable();
                    this.documentForm.controls.preamble.enable();
                    this.documentForm.controls.isNew.enable();
                    this.documentForm.controls.isUsed.enable();
                    this.documentForm.controls.isBuy.enable();
                    this.documentForm.controls.isLease.enable();
                    this.documentForm.controls.isCash.enable();
                    this.documentForm.controls.isFinance.enable();
                    this.documentForm.controls.isLeaseTurnIn.enable();
                    this.documentForm.controls.isTradeIn.enable();
                    this.documentForm.controls.isRequiredForAll.enable();
                    this.documentForm.controls.typeOfDocument.enable();
                    this.documentForm.controls.isDealerOnly.enable();
                }
            });
    }

    public radioChange(e: MatRadioChange) {
        switch (e.value as FormDocumentUploadType) {
            case FormDocumentUploadType.Predefined_Form:
                this.selectedFormType = 'predefined-form';
                break;
            case FormDocumentUploadType.Uploaded_During_Deal:
            case FormDocumentUploadType.Consumer_Documents:
                this.selectedFormType = 'upload-during-deal';
                break;
            case FormDocumentUploadType.Print_From_PDF:
                this.selectedFormType = 'print-from-pdf';
                break;
            case FormDocumentUploadType.Print_From_Impact_Form:
                this.selectedFormType = 'print-from-impact';
                break;
            case FormDocumentUploadType.Manually_Filled_During_Deal:
                this.documentForm.patchValue({ fileProvidedBy: Assignees.dealer });
                this.documentForm.controls.fileProvidedBy.disable();
                this.selectedFormType = 'manually-filled-during-deal';
                break;
            default:
                this.selectedFormType = 'predefined-form';
        }

        if (this.selectedFormType !== 'manually-filled-during-deal') {
            this.documentForm.controls.fileProvidedBy.enable();
        }
    }

    public showFileSelection() {
        return (
            !this.selectedPdf ||
            (this.selectedPdf && this.selectedPdf !== null && !this.selectedPdf.commonDocument && (!this.selectedPdf.systemDocument || this.userIsInternal()))
        );
    }

    public async openFileChooser(event: Event) {
        if (!this.selectedPdf) {
            (<HTMLInputElement>event.target).value = null;
        } else {
            event.preventDefault();

            if (this.title === 'dealer-view-template' || this.title === 'dealer-edited-existing' || this.title === 'cancel-existing-edit') {
                this.title = 'edit-existing';
            } else {
                this.title = 'edit-create';
            }

            this.router.navigate(['dashboard', 'forms', 'edit'], {
                state: {
                    docId: this.docId,
                    file: this.selectedPdf,
                    form: this.documentForm.value,
                    fields: this.pdfFields,
                    internalDoc: this.internalDocumentTypeSelected,
                    title: this.title,
                },
            });
        }
    }

    private async prepareData(): Promise<any> {
        const fields = this.documentForm.getRawValue();
        const formData: FormData = new FormData();
        const fileData: FormData = new FormData();

        let fileSize;
        //DEAL FIELDS
        formData.append(
            'dealFields',
            JSON.stringify({
                isNew: fields.isNew,
                isUsed: fields.isUsed,
                isBuy: fields.isBuy,
                isLease: fields.isLease,
                isCash: fields.isCash,
                isFinance: fields.isFinance,
                isLeaseTurnIn: fields.isLeaseTurnIn,
                isTradeIn: fields.isTradeIn,
            })
        );

        //REQUIREMENTS
        formData.append(
            'requirements',
            JSON.stringify(<DdjDocumentRequirements>{
                [DdjDocumentRequirementFields.mandatoryDocument]: false,
                [DdjDocumentRequirementFields.isDealerOnly]: fields.isDealerOnly,
                [DdjDocumentRequirementFields.needsBuyerUpload]: fields.fileProvidedBy === Assignees.buyer,
                [DdjDocumentRequirementFields.needsCoBuyerUpload]: fields.fileProvidedBy === Assignees.cobuyer,
                [DdjDocumentRequirementFields.needsDealerUpload]: fields.fileProvidedBy === Assignees.dealer,
            })
        );

        // Clear Document if Uploaded_During_Deal type
        if (this.documentForm.controls.typeOfDocument.value === FormDocumentUploadType.Uploaded_During_Deal) {
            fileSize = null;
            this.fileName = null;
            this.selectedPdf = null;
        }

        //PDF
        if (this.selectedPdf && this.selectedPdf !== null && this.documentForm.controls.typeOfDocument.value !== FormDocumentUploadType.Uploaded_During_Deal) {
            // we were embedding the fields, but no longer are. They need to be applied in real-time as needed
            formData.append('pdfFields', JSON.stringify(this.pits.fields));
            if (window.location.pathname.includes('deals')) {
                const docInfo = await this.setDocToBase64Json();
                formData.append('file', docInfo?.docJson);
                fileSize = docInfo?.fileSize;

                //User Fields
                formData.append(
                    'userFields',
                    JSON.stringify({
                        dealerId: this.user.dealerId,
                    })
                );
            } else {
                // cleaning a pdf - need to determine base64 and file size on edit-form component since there is no instance of pdftron
                // if there were an instance of pdf tron it would have to display the unclean pdf so we could grab the data
                // then we would have to prepare the data, save it and redraw the cleaned data
                if (this.title.includes('dealer-cleaned')) {
                    formData.append('file', JSON.stringify({ base64File: this.cleanedBase64, filename: this.fileName }));
                    fileSize = this.cleanedFileSize;
                } else if (this.documentForm.controls.typeOfDocument.value !== FormDocumentUploadType.Uploaded_During_Deal) {
                    if (!this.apiSigningEnabled) {
                        const base64 = await prepareFileForUploadBase64(this.pits.pdfTron, false);
                        if (base64) {
                            formData.append('file', JSON.stringify({ base64File: base64, filename: this.fileName }));
                            fileSize = Math.round(base64.replace(/=/g, '').length * 0.75) ?? 0;
                        }
                    } else {
                        const docInfo = await this.setDocToBase64Json();
                        formData.append('file', docInfo?.docJson);
                        fileSize = docInfo?.fileSize;
                    }
                }
            }
        }

        //User Fields
        formData.append(
            'dealerFields',
            JSON.stringify({
                dealerId: this.user.dealerId,
            })
        );

        // need to get template data if we are on the deals page - adding a doc to a deal and there is a docId
        // meaning we are not creating a new doc to add to this deal
        if (window.location.pathname.includes('deals') && this.docId) {
            //hitting node.js
            const template = await firstValueFrom(this.documentsService.getFullTemplateData$(this.docId));
            this.templateData.documentId = template.documentId;
            this.templateData.offsetX = template.offsetX;
            this.templateData.offsetY = template.offsetY;
        }

        //DOCUMENT FIELDS
        formData.append(
            'documentFields',
            JSON.stringify({
                fileName: this.fileName !== null && this.fileName == 'Choose File' ? null : this.fileName,
                size: fileSize ? fileSize : 0,
                displayName: fields.name,
                preamble: fields.preamble,
                documentId: this.docId,
                internalDocumentTypeId: this.internalDocumentTypeSelected?.internalDocumentTypeId
                    ? this.internalDocumentTypeSelected.internalDocumentTypeId
                    : this.internalDocumentTypeSelected,
                readyForSignatures: this.getInternalDocType()[0]?.dataLoadFlag,
                uploadTypeId: this.getInternalDocType()[0]?.uploadTypeId,
                offsetX: this.templateData.offsetX,
                offsetY: this.templateData.offsetY,
                templateDocumentId: this.templateData.documentId,
            })
        );

        return { formData, fileData };
    }

    private async setDocToBase64Json(): Promise<any> {
        // anything except cleaning a pdf
        const base64 = await prepareFileForUploadBase64(this.pits.pdfTron, false);
        if (base64) {
            const docJson = JSON.stringify({ base64File: base64, filename: this.fileName });
            const fileSize = Math.round(base64.replace(/=/g, '').length * 0.75) ?? 0;

            return { docJson, fileSize };
        }

        return null;
    }

    private saveDocToDeal(data: any) {
        this.documentsService
            .createDealDocumentV2$(data.formData, this.dealId)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.errorService.showGenericError();
                    this.isLoading = false;
                    throw error;
                })
            )
            .subscribe((success) => {
                if (success) {
                    this.documentsService
                        .getTemplateBasicList$(true, this.user.dealerId)
                        .pipe(takeWhile(() => this.isComponentAlive))
                        .subscribe((templates) => {
                            this.coreForms = templates;
                            this.isLoading = false;
                            this.dialog
                                .open(SuccessModalComponent, {
                                    width: '700px',
                                    closeOnNavigation: true,
                                    data: {
                                        title: 'Form Added',
                                        content: 'Form has been added to deal!',
                                    },
                                })
                                .afterClosed()
                                .pipe(takeWhile(() => this.isComponentAlive))
                                .subscribe(() => {
                                    this.router.navigate(['dashboard', 'deals', this.dealId]);
                                });
                        });
                }
            });
    }

    addFormToDealMode(): boolean {
        return window.location.pathname.includes('deals');
    }

    public async saveForm(): Promise<void> {
        if (!this.isValid()) {
            return;
        }

        // Verify the document is valid if marked as isDealerOnly
        if (this.documentForm.value.isDealerOnly && this.pits.isInvalidDealerOnly(this.pdfFields)) {
            this.pits.displayInvalidDealerOnlyMessage();
            return;
        }

        this.isLoading = true;
        const dataToSave = await this.prepareData();

        if (this.addFormToDealMode()) {
            this.saveDocToDeal(dataToSave);
            return;
        }

        if (dataToSave) {
            this.documentsService
                .createTemplate$(dataToSave.formData)
                .pipe(
                    takeWhile(() => this.isComponentAlive),
                    catchError((error) => {
                        this.errorService.showGenericError();
                        this.isLoading = false;
                        throw error;
                    })
                )
                .subscribe((template) => {
                    if (template?.documentId) {
                        this.fileName = template.fileName;
                        this.docId = template.documentId;
                        this.documentsService
                            .getTemplateBasicList$(true, this.user.dealerId)
                            .pipe(takeWhile(() => this.isComponentAlive))
                            .subscribe((templates) => {
                                this.coreForms = templates;

                                //get the selected document.
                                const currentSelectedDoc = templates.find((x) => x.documentId === template.documentId);
                                let form: DdjTemplate = null;
                                this.documentsService
                                    .getFullTemplateData$(currentSelectedDoc.documentId, true)
                                    .pipe(takeWhile(() => this.isComponentAlive))
                                    .subscribe((fullTemplate) => {
                                        form = fullTemplate;
                                        if (form.documentSignedURL) {
                                            this.selectedPdfHasDocUrl = true;
                                        }

                                        // if manually uploaded during the deal, hide the pdf display because there is no pdf
                                        if (!form.fileName) {
                                            this.pdfDataSet = false;
                                        } else {
                                            // find the new template in the array of forms so the data is the way we need it
                                            this.selectedPdf = form;
                                            // set all the info as we would when someone clicks on a form editForm
                                            this.selectedPdf.status = {
                                                ...this.selectedPdf.status,
                                                selected: true,
                                            };
                                            this.docId = this.selectedPdf.documentId;
                                            this.templateData.offsetX = this.selectedPdf.offsetX;
                                            this.templateData.offsetY = this.selectedPdf.offsetY;
                                        }

                                        this.isLoading = false;

                                        if (this.title?.includes('cleaned')) {
                                            this.title = 'dealer-view-template';
                                            this.documentForm.markAsPristine();
                                            this.dialog
                                                .open(SuccessModalComponent, {
                                                    width: '700px',
                                                    closeOnNavigation: true,
                                                    data: {
                                                        title: 'Form Cleared',
                                                        content: `${template.displayName} has been cleaned!`,
                                                    },
                                                })
                                                .afterClosed()
                                                .pipe(takeWhile(() => this.isComponentAlive))
                                                .subscribe(() => {
                                                    this.pdfDataSet = true;
                                                });
                                        } else {
                                            this.title = 'dealer-view-template';
                                            this.documentForm.markAsPristine();
                                            this.dialog
                                                .open(SuccessModalComponent, {
                                                    width: '700px',
                                                    closeOnNavigation: true,
                                                    data: {
                                                        title: 'F&I Form Saved',
                                                        content: `<p><b>${template.displayName}</b> has been saved. If you have modified an existing form, the updated version will be used in future Deal Jackets. These changes do not update any existing Deal Jackets that include this form. If you need to replace it in a Deal Jacket, delete the old version and add the updated one.</p>`,
                                                    },
                                                })
                                                .afterClosed()
                                                .pipe(takeWhile(() => this.isComponentAlive))
                                                .subscribe(() => {
                                                    this.createClicked = false;
                                                    this.isDeleteAllowed = this.allowTemplateDelete(template) || this.handleSystemDocumentsCheck();
                                                });
                                        }
                                    });
                            });
                    } else {
                        this.isLoading = false;
                        this.errorService.showGenericError();
                    }
                });
        }
    }

    private selectPdf$(file, fileName?: string): Observable<void> {
        return of(null).pipe(
            takeWhile(() => this.isComponentAlive),
            tap(() => {
                this.isLoading = true;
                this.selectedPdf = file;
                this.fileName = fileName || this.selectedPdf?.name;
                if (window.location.pathname.includes('deals')) {
                    this.router.navigate(['dashboard', 'deals', this.dealId, 'add', 'edit'], {
                        state: {
                            docId: this.docId,
                            file,
                            form: this.documentForm.value,
                            fields: this.pdfFields,
                            internalDoc: this.internalDocumentTypeSelected,
                            title: 'edit-deal-doc',
                        },
                    });
                } else {
                    this.router.navigate(['dashboard', 'forms', 'edit'], {
                        state: {
                            docId: this.docId,
                            file,
                            form: this.documentForm.value,
                            fields: this.pdfFields,
                            internalDoc: this.internalDocumentTypeSelected,
                            title: 'dealer-create',
                        },
                    });
                }
            }),
            catchError((error) => {
                this.isLoading = false;
                this.errorService.showGenericError();
                throw error;
            })
        );
    }

    public selectPdfForEditing(file): void {
        if (file.type === 'image/jpeg' || file.type.includes('pdf')) {
            const fileSizeInMegabytes = file.size / 1048576;

            if (fileSizeInMegabytes >= 25) {
                this.dialog.open(FileSizeErrorModalComponent, {
                    width: '700px',
                    closeOnNavigation: false,
                    autoFocus: true,
                    data: {
                        isMultipleUpload: false,
                    },
                });
            } else {
                if (file.name.length > 100) {
                    this.errorService.showError('The file name cannot exceed 100 characters.');
                    return;
                }
                this.selectPdf$(file)
                    .pipe(takeWhile(() => this.isComponentAlive))
                    .subscribe(() => {
                        this.selectedPdf = file;
                    });
            }
        } else {
            this.errorService.showError('The file uploaded must be a jpeg or pdf.');
        }
    }

    selectForm(form: any) {
        this.docId = form.documentId;
        this.isLoading = true;
        const currentSelectedDoc = this.coreForms.find((x) => x.documentId === this.docId);
        this.documentsService
            .getFullTemplateData$(currentSelectedDoc.documentId, true)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((fullTemplate) => {
                form = fullTemplate;
                this.selectedPdfHasDocUrl = true;

                //get full document template data form
                this.fileName = form.fileName;
                const formData = {
                    ...form.deal,
                    docId: form.documentId,
                    name: form.displayName,
                    preamble: form.preamble,
                    isDealerOnly: form.requirements.isDealerOnly,
                    internalDoc: form.internalDocumentTypeId,
                    fileProvidedBy: this.getFileProvidedBy(form.requirements),
                    typeOfDocument: this.getInternalDocType(form.uploadTypeId)[0]?.uploadTypeId,
                };

                this.selectedPdfIsCommonDocument = form.commonDocument;
                this.selectedPdfIsSystemDocument = form.systemDocument;
                this.pdfFields = form.inputFieldSettings;

                if (form.fileName) {
                    this.selectedPdf = form;
                    this.pdfDataSet = true;
                    this.title = 'dealer-view-template';
                    this.fileName = form.fileName;
                }
                this.setForm(formData);
                this.isDeleteAllowed = this.allowTemplateDelete(form) || this.handleSystemDocumentsCheck();
                this.isLoading = false;
            });
    }

    private setForm(formData: any): void {
        if (formData.internalDoc) {
            this.internalDocumentTypeSelected = formData.internalDoc;
        }

        if (formData.typeOfDocument === FormDocumentUploadType.Uploaded_During_Deal) {
            this.cancelUpload();
        }

        this.docId = formData.docId;

        this.documentForm.patchValue({
            fileProvidedBy: formData.fileProvidedBy,
            isBuy: formData.isBuy,
            isCash: formData.isCash,
            isDealerOnly: formData.isDealerOnly,
            isFinance: formData.isFinance,
            isLease: formData.isLease,
            isLeaseTurnIn: formData.isLeaseTurnIn,
            isNew: formData.isNew,
            isRequiredForAll: formData.isRequiredForAll,
            isTradeIn: formData.isTradeIn,
            isUsed: formData.isUsed,
            name: formData.name,
            preamble: formData.preamble,
            typeOfDocument: formData.typeOfDocument,
        });
    }

    private setPageInfo(history: any): void {
        if (history.state.title?.includes('edited') || history.state.title?.includes('cancel') || history.state.title?.includes('create-view')) {
            const routeData = history.state;
            this.docId = routeData.docId;
            this.title = routeData.title;
            this.fileName = routeData.file.file.name ? routeData.file.file.name : routeData.file.file.fileName;
            this.pdfFields = !history.state.title?.includes('cancel') ? routeData.file.fields : routeData.fields;
            this.pdfDataSet = true;
            this.selectedPdf = routeData.file.file;
            this.internalDocumentTypeSelected = routeData.form.internalDoc;
            this.setForm({ ...routeData.form.form, docId: this.docId });

            // we don't want to show the delete button when we come back from editing a new template
            if (this.docId) {
                this.isDeleteAllowed = this.allowTemplateDelete(routeData.file.file) || this.handleSystemDocumentsCheck();
            }
        }

        if (history.state.title?.includes('cleaned')) {
            const routeData = history.state;
            this.cleanedBase64 = routeData.base64;
            this.cleanedFileSize = routeData.fileSize;
            this.docId = routeData.docId;
            this.title = routeData.title;
            this.fileName = routeData.file.file.name ? routeData.file.file.name : routeData.file.file.fileName;
            this.pdfDataSet = false;
            this.selectedPdf = routeData.file.file;
            this.pdfFields = routeData.file.fields;
            this.internalDocumentTypeSelected = routeData.form.internalDoc;
            this.setForm({ ...routeData.form.form, docId: this.docId });
            this.isDeleteAllowed = this.allowTemplateDelete(routeData.file.file) || this.handleSystemDocumentsCheck();
            setTimeout(() => {
                this.saveForm();
            }, 100);
        }
    }

    private updateFormValidity(): void {
        Object.keys(this.documentForm.controls).forEach((field) => {
            const control = this.documentForm.get(field);
            control.updateValueAndValidity();
        });
    }

    //Remove once uploadTypeId 5 and 6 are enabled
    public tempDisableType5And6(documentTypeIdSelection): boolean {
        //5 = Manually Filled During The Deal and 6 = Dealer Uploaded with Predefine Signatures
        return documentTypeIdSelection === 5 || documentTypeIdSelection === 6;
    }

    //Remove once uploadTypeId 5 and 6 are enabled
    public tempDisableType5And6Text(documentTypeIdSelection): string {
        let message;
        if (this.tempDisableType5And6(documentTypeIdSelection)) {
            message = 'Feature disabled temporarily. You may change the document type while keeping the same pdf and signature fields.';
        }

        return message;
    }
}
